import { ApplicationConfig, inject, provideBrowserGlobalErrorListeners } from '@angular/core';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideRouter, Routes } from '@angular/router';
import { Router } from '@angular/router';
import { catchError, map, of, throwError } from 'rxjs';
import { AuthService } from './core/auth/auth.service';
import { ApiClientService } from './core/api/api-client.service';
import { DashboardPageComponent } from './features/dashboard/dashboard-page.component';
import { DriversPageComponent } from './features/drivers/drivers-page.component';
import { DriverDetailPageComponent } from './features/drivers/driver-detail-page.component';
import { DriverEditPageComponent } from './features/drivers/driver-edit-page.component';
import { DriverEvaluationReportPageComponent } from './features/drivers/driver-evaluation-report-page.component';
import { CompaniesPageComponent } from './features/companies/companies-page.component';
import { CompanyDetailPageComponent } from './features/companies/company-detail-page.component';
import { OrganizationsPageComponent } from './features/organizations/organizations-page.component';
import { AuditLogPageComponent } from './features/audit/audit-log-page.component';
import { CoursesPageComponent } from './features/courses/courses-page.component';
import { CourseVideosPageComponent } from './features/course-videos/course-videos-page.component';
import { LearningPageComponent } from './features/learning/learning-page.component';
import { LessonPageComponent } from './features/learning/lesson-page.component';
import { KnowledgeCheckPageComponent } from './features/learning/knowledge-check-page.component';
import { FinalKnowledgeCheckPageComponent } from './features/learning/final-knowledge-check-page.component';
import { CertificatesPageComponent } from './features/certificates/certificates-page.component';
import { CertificateDetailPageComponent } from './features/certificates/certificate-detail-page.component';
import { CertificateTemplatePageComponent } from './features/certificates/certificate-template-page.component';
import { LoginPageComponent } from './features/auth/login-page.component';
import { ForgotPasswordPageComponent } from './features/auth/forgot-password-page.component';
import { ResetPasswordPageComponent } from './features/auth/reset-password-page.component';
import { PaymentPageComponent } from './features/payment/payment-page.component';
import { PublicCertificateVerificationPageComponent } from './features/certificates/public-certificate-verification-page.component';

const routes: Routes = [
  { path: 'login', component: LoginPageComponent },
  { path: 'forgot-password', component: ForgotPasswordPageComponent },
  { path: 'reset-password', component: ResetPasswordPageComponent },
  { path: 'verify/certificates/:token', component: PublicCertificateVerificationPageComponent },
  { path: 'dashboard', component: DashboardPageComponent, canActivate: [managementGuard] },
  { path: 'organizations', component: OrganizationsPageComponent, canActivate: [superAdminGuard] },
  { path: 'companies', component: CompaniesPageComponent, canActivate: [managementGuard] },
  { path: 'companies/:id', component: CompanyDetailPageComponent, canActivate: [managementGuard] },
  { path: 'drivers', component: DriversPageComponent, canActivate: [managementGuard] },
  { path: 'drivers/:id/edit', component: DriverEditPageComponent, canActivate: [managementGuard] },
  { path: 'drivers/:driverId/evaluations/:enrollmentId', component: DriverEvaluationReportPageComponent, canActivate: [managementGuard] },
  { path: 'drivers/:id', component: DriverDetailPageComponent, canActivate: [managementGuard] },
  { path: 'courses', component: CoursesPageComponent, canActivate: [authenticatedGuard] },
  { path: 'course-videos', component: CourseVideosPageComponent, canActivate: [courseEditorGuard] },
  { path: 'audit-logs', component: AuditLogPageComponent, canActivate: [superAdminGuard] },
  { path: 'payment', component: PaymentPageComponent, canActivate: [driverGuard] },
  { path: 'learning/:enrollmentId/lessons/:lessonId', component: LessonPageComponent, canActivate: [paidDriverGuard] },
  { path: 'learning/:enrollmentId/lessons/:lessonId/knowledge-check', component: KnowledgeCheckPageComponent, canActivate: [paidDriverGuard] },
  { path: 'learning/:enrollmentId/final-knowledge-check/result', component: FinalKnowledgeCheckPageComponent, canActivate: [paidDriverGuard] },
  { path: 'learning/:enrollmentId/final-knowledge-check', component: FinalKnowledgeCheckPageComponent, canActivate: [paidDriverGuard] },
  { path: 'learning', component: LearningPageComponent, canActivate: [paidDriverGuard] },
  { path: 'certificates/:id', component: CertificateDetailPageComponent, canActivate: [authenticatedGuard] },
  { path: 'certificates', component: CertificatesPageComponent, canActivate: [authenticatedGuard] },
  { path: 'certificate-template', component: CertificateTemplatePageComponent, canActivate: [superAdminGuard] },
  { path: '', pathMatch: 'full', redirectTo: 'dashboard' },
  { path: '**', redirectTo: 'dashboard' }
];

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes),
    provideHttpClient(withInterceptors([authInterceptor]))
  ]
};

function authenticatedGuard(): boolean {
  const auth = inject(AuthService);
  const router = inject(Router);

  if (auth.isAuthenticated()) {
    return true;
  }

  router.navigateByUrl('/login');
  return false;
}

function managementGuard(): boolean {
  const auth = inject(AuthService);
  const router = inject(Router);

  if (auth.hasAnyRole(['SUPER_ADMIN', 'ORGANIZATION_ADMIN', 'COMPANY_ADMIN', 'QUALITY_MANAGER'])) {
    return true;
  }

  router.navigateByUrl(auth.isAuthenticated() ? '/learning' : '/login');
  return false;
}

function superAdminGuard(): boolean {
  const auth = inject(AuthService);
  const router = inject(Router);

  if (auth.hasAnyRole(['SUPER_ADMIN'])) {
    return true;
  }

  router.navigateByUrl(auth.isAuthenticated() ? '/dashboard' : '/login');
  return false;
}

function driverGuard(): boolean {
  const auth = inject(AuthService);
  const router = inject(Router);

  if (auth.hasAnyRole(['DRIVER'])) {
    return true;
  }

  router.navigateByUrl(auth.isAuthenticated() ? '/dashboard' : '/login');
  return false;
}

function paidDriverGuard(): import('@angular/router').GuardResult | import('rxjs').Observable<import('@angular/router').GuardResult> {
  const auth = inject(AuthService);
  const api = inject(ApiClientService);
  const router = inject(Router);

  if (!auth.hasAnyRole(['DRIVER'])) {
    router.navigateByUrl(auth.isAuthenticated() ? '/dashboard' : '/login');
    return false;
  }

  return api.driverPaymentStatus().pipe(
    map((payment) => payment.paid ? true : router.createUrlTree(['/payment'])),
    catchError(() => of(router.createUrlTree(['/payment'])))
  );
}

function courseEditorGuard(): boolean {
  const auth = inject(AuthService);
  const router = inject(Router);

  if (auth.hasAnyRole(['SUPER_ADMIN'])) {
    return true;
  }

  router.navigateByUrl(auth.isAuthenticated() ? '/dashboard' : '/login');
  return false;
}

function authInterceptor(request: import('@angular/common/http').HttpRequest<unknown>, next: import('@angular/common/http').HttpHandlerFn) {
  const auth = inject(AuthService);
  const token = auth.token();

  const authenticatedRequest = token
    ? request.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
    : request;

  return next(authenticatedRequest).pipe(
    catchError((error) => {
      if (error.status === 401) {
        auth.logout();
      }

      return throwError(() => error);
    })
  );
}
